import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import {
   getWellnessEntries,
   createWellnessEntry,
   updateWellnessEntry,
   deleteWellnessEntry,
   getWellnessEntryByDate,
} from '../../../api/Wellness';
import {
   setWellnessEntries,
   setWellnessLoading,
   setWellnessError,
   addWellnessEntry,
   updateWellnessEntryAction,
   removeWellnessEntry,
   setCurrentEntry,
   setFormDraft,
   clearFormDraft,
} from '../../../redux/actions/Wellness';

export const useWellnessEntries = (month, year) => {
   const dispatch = useDispatch();
   const { entries, loading, error, currentEntry, formDraft } = useSelector(
      state => state.wellness
   );

   const fetchEntries = useCallback(async () => {
      dispatch(setWellnessLoading(true));
      try {
         const data = await getWellnessEntries(month, year);
         dispatch(setWellnessEntries(data));
      } catch (err) {
         dispatch(setWellnessError(err.message || 'Failed to fetch entries'));
         notification.error({
            message: 'Error',
            description: err.message || 'Failed to fetch wellness entries',
         });
      } finally {
         dispatch(setWellnessLoading(false));
      }
   }, [dispatch, month, year]);

   useEffect(() => {
      if (month !== undefined && year !== undefined) {
         fetchEntries();
      }
   }, [month, year, fetchEntries]);

   const getEntryByDate = useCallback(
      async date => {
         try {
            const entry = await getWellnessEntryByDate(date);
            dispatch(setCurrentEntry(entry));
            return entry;
         } catch (err) {
            notification.error({
               message: 'Error',
               description: err.message || 'Failed to fetch entry',
            });
            return null;
         }
      },
      [dispatch]
   );

   const createEntry = useCallback(
      async data => {
         dispatch(setWellnessLoading(true));
         try {
            const newEntry = await createWellnessEntry(data);
            dispatch(addWellnessEntry(newEntry));
            dispatch(clearFormDraft());
            notification.success({
               message: 'Success',
               description: 'Wellness entry saved successfully',
            });
            return newEntry;
         } catch (err) {
            notification.error({
               message: 'Error',
               description: err.message || 'Failed to save entry',
            });
            throw err;
         } finally {
            dispatch(setWellnessLoading(false));
         }
      },
      [dispatch]
   );

   const updateEntry = useCallback(
      async (id, data) => {
         dispatch(setWellnessLoading(true));
         try {
            const updatedEntry = await updateWellnessEntry(id, data);
            dispatch(updateWellnessEntryAction(updatedEntry));
            dispatch(clearFormDraft());
            notification.success({
               message: 'Success',
               description: 'Wellness entry updated successfully',
            });
            return updatedEntry;
         } catch (err) {
            notification.error({
               message: 'Error',
               description: err.message || 'Failed to update entry',
            });
            throw err;
         } finally {
            dispatch(setWellnessLoading(false));
         }
      },
      [dispatch]
   );

   const deleteEntry = useCallback(
      async id => {
         dispatch(setWellnessLoading(true));
         try {
            await deleteWellnessEntry(id);
            dispatch(removeWellnessEntry(id));
            dispatch(setCurrentEntry(null));
            notification.success({
               message: 'Success',
               description: 'Wellness entry deleted',
            });
         } catch (err) {
            notification.error({
               message: 'Error',
               description: err.message || 'Failed to delete entry',
            });
            throw err;
         } finally {
            dispatch(setWellnessLoading(false));
         }
      },
      [dispatch]
   );

   const saveDraft = useCallback(
      data => {
         dispatch(setFormDraft(data));
      },
      [dispatch]
   );

   const clearDraft = useCallback(() => {
      dispatch(clearFormDraft());
   }, [dispatch]);

   const selectEntry = useCallback(
      entry => {
         dispatch(setCurrentEntry(entry));
      },
      [dispatch]
   );

   const getEntriesForDate = useCallback(
      date => {
         return entries.filter(entry => entry.date === date);
      },
      [entries]
   );

   const hasEntryForDate = useCallback(
      date => {
         return entries.some(entry => entry.date === date);
      },
      [entries]
   );

   return {
      entries,
      loading,
      error,
      currentEntry,
      formDraft,
      fetchEntries,
      getEntryByDate,
      createEntry,
      updateEntry,
      deleteEntry,
      saveDraft,
      clearDraft,
      selectEntry,
      getEntriesForDate,
      hasEntryForDate,
   };
};
